import type { ChartOptions, TooltipItem } from 'chart.js';
import type { Dispatch, SetStateAction } from 'react';
import {
  DOUGHNUT_CIRCUMFERENCE,
  DOUGHNUT_CUTOUT_PERCENT,
  DOUGHNUT_HOVER_OFFSET,
  DOUGHNUT_RING_BORDER_COLOR,
  DOUGHNUT_RING_BORDER_WIDTH,
  DOUGHNUT_ROTATION_DEG,
  TOOLTIP_RISK_ACCENT_COLORS,
  type CounterpartySignalChartCategory,
} from './ConnectionChart.constants';

export interface ConnectionChartData {
  labels: string[];
  datasets: {
    data: number[];
    backgroundColor: string[];
    segmentRiskKeys?: CounterpartySignalChartCategory[];
  }[];
}

export interface ConnectionChartTooltipState {
  opacity: number;
  label?: string;
  left: number;
  top: number;
  color?: string;
}

export function mergeDoughnutChartData(chartData: ConnectionChartData) {
  return {
    labels: chartData.labels,
    datasets: chartData.datasets.map((ds) => ({
      ...ds,
      borderWidth: DOUGHNUT_RING_BORDER_WIDTH,
      borderColor: DOUGHNUT_RING_BORDER_COLOR,
      hoverBorderWidth: DOUGHNUT_RING_BORDER_WIDTH,
      hoverBorderColor: DOUGHNUT_RING_BORDER_COLOR,
      hoverOffset: DOUGHNUT_HOVER_OFFSET,
    })),
  };
}

function indexOfDominantValue(values: number[]): number {
  return values.reduce(
    (bestIdx, v, i) => (v > values[bestIdx] ? i : bestIdx),
    0,
  );
}

export function splitTooltipPercentAndName(label: string): {
  percent: string;
  name: string;
} {
  const m = label.match(/^\s*(\d+(?:[.,]\d+)?\s*%)\s*(.*)$/u);
  if (m) {
    return { percent: m[1].trim(), name: m[2].trim() };
  }
  return { percent: '', name: label.trim() };
}

export function getDominantSegmentStats(chartData: ConnectionChartData): {
  dominant: number | null;
} {
  const values = chartData.datasets[0]?.data ?? [];
  if (!values.length) {
    return { dominant: null };
  }
  const i = indexOfDominantValue(values);
  return {
    dominant: Math.round(values[i]),
  };
}

export function chartHasPositiveSegments(
  chartData: ConnectionChartData,
): boolean {
  const values = chartData.datasets[0]?.data ?? [];
  return values.length > 0 && values.some((n) => n > 0);
}

function tooltipAccentColorFromDataPoint(
  dataPoint: TooltipItem<'doughnut'>,
): string {
  const ds = dataPoint.dataset;
  if ('segmentRiskKeys' in ds && Array.isArray(ds.segmentRiskKeys)) {
    const raw = ds.segmentRiskKeys[dataPoint.dataIndex];
    if (raw === 'trusted') {
      return TOOLTIP_RISK_ACCENT_COLORS.trusted;
    }
    if (raw === 'suspicious') {
      return TOOLTIP_RISK_ACCENT_COLORS.suspicious;
    }
    if (raw === 'danger') {
      return TOOLTIP_RISK_ACCENT_COLORS.danger;
    }
  }
  const bg = dataPoint.dataset.backgroundColor;
  if (typeof bg === 'string') {
    return bg;
  }
  if (Array.isArray(bg)) {
    const slice = bg[dataPoint.dataIndex];
    return typeof slice === 'string' ? slice : '';
  }
  return '';
}

export function buildDoughnutChartOptions(
  setTooltipData: Dispatch<SetStateAction<ConnectionChartTooltipState | null>>,
): ChartOptions<'doughnut'> {
  const hideTooltip = () => {
    setTooltipData((prev) => (prev ? { ...prev, opacity: 0 } : null));
  };

  return {
    plugins: {
      tooltip: {
        enabled: false,
        external: (ctx) => {
          const { tooltip } = ctx;

          if (tooltip.opacity === 0 || !tooltip.body?.length) {
            hideTooltip();
            return;
          }

          const dataPoint = tooltip.dataPoints[0];
          setTooltipData({
            opacity: tooltip.opacity,
            label: String(dataPoint.label),
            left: tooltip.caretX,
            top: tooltip.caretY,
            color: tooltipAccentColorFromDataPoint(dataPoint),
          });
        },
      },
    },
    maintainAspectRatio: false,
    cutout: DOUGHNUT_CUTOUT_PERCENT,
    rotation: DOUGHNUT_ROTATION_DEG,
    circumference: DOUGHNUT_CIRCUMFERENCE,
  };
}
